<template>
    <div class="main-panel">
        <div v-if="order">
            <div class="result-title">
                <h1>User ID: {{ order.userId }}</h1>

                <a class="user" :href="`/users/${order.userId}`">
                    <h1>
                        User Details
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.--><path d="M320 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l82.7 0L201.4 265.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L448 109.3l0 82.7c0 17.7 14.3 32 32 32s32-14.3 32-32l0-160c0-17.7-14.3-32-32-32L320 0zM80 32C35.8 32 0 67.8 0 112L0 432c0 44.2 35.8 80 80 80l320 0c44.2 0 80-35.8 80-80l0-112c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 112c0 8.8-7.2 16-16 16L80 448c-8.8 0-16-7.2-16-16l0-320c0-8.8 7.2-16 16-16l112 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L80 32z" /></svg>
                    </h1>
                </a>
            </div>
            <div class="order-grid">
                <div class="content">
                    <div class="section">
                        <h3>Order Details</h3>

                        <label for="orderid"><strong>Order ID</strong></label>
                        <input id="orderid"
                               name="orderid"
                               disabled
                               :value="order.orderId"
                               readonly />

                        <label for="date"><strong>Order Date</strong></label>
                        <input id="date"
                               name="date"
                               disabled
                               :value="order.createDate"
                               readonly />
                    </div>
                </div>

                <div class="content">
                    <div class="section">
                        <h3>Entries</h3>

                        <input v-for="(entrySummary, index) in order.entrySummaries"
                               :key="index"
                               class="entrySummary"
                               name="entrySummaries"
                               disabled
                               :value="entrySummary"
                               readonly />
                    </div>
                </div>

                <div class="content">
                    <div class="section">
                        <h3>Payment Details</h3>

                        <div v-for="(payment, index) in order.paymentDetails"
                             :key="index"
                             class="paymentDetail">
                            <label :for="`payment-${index}`" class="payment-label">
                                {{ payment.paymentMethod.toUpperCase() }}
                            </label>
                            <input :id="`payment-${index}`"
                                   :name="`payment-${index}`"
                                   disabled
                                   :value="`£${payment.amount.toFixed(2)}`"
                                   readonly
                                   class="payment-input" />
                        </div>

                        <label v-if="order.freeEntries > 0" for="freeEntries"><strong>Free Entries</strong></label>
                        <input v-if="order.freeEntries > 0"
                               id="freeEntries"
                               name="freeEntries"
                               disabled
                               :value="order.freeEntries.toString()"
                               readonly />

                        <label for="total"><strong>Total</strong></label>
                        <input id="total"
                               name="total"
                               disabled
                               :value="`£${order.totalAmount.toFixed(2)}`"
                               readonly />
                    </div>

                </div>

                <div class="content">
                    <div class="section">
                        <button :disabled="isSubmittingUpdateDetails"
                                class="btn"
                                @click="sendOrderEmail">
                            <span v-if="!isSubmittingUpdateDetails">Send Confirmation Email</span>
                            <LoaderDots v-if="isSubmittingUpdateDetails" />
                        </button>

                    </div>
                </div>
            </div>
        </div>
    </div>


</template>

<script lang="ts">
    import { defineComponent, onMounted, ref } from "vue";
    import { useRoute } from "vue-router";
    import getOrderDetailsByIdResponse from "../responses/GetOrderDetailsByIdResponse";
    import orderService from "../services/order-service";
    import { showAlert } from "../utils/global";

    export default defineComponent({
        setup() {
            const route = useRoute();
            const orderId = route.params.orderId.toString();
            const order = ref<getOrderDetailsByIdResponse | null>(null);
            const isSubmittingUpdateDetails = ref(false);

            const getOrderDetails = async () => {
                try {
                    order.value = await orderService.getOrderDetailsById(orderId);
                } catch (error) {
                    showAlert(
                        "Error",
                        "Failed to fetch order details. Please try again later.",
                        "error"
                    );
                }
            };

            const sendOrderEmail = async () => {

                isSubmittingUpdateDetails.value = true;

                orderService.sendOrderEmail(orderId);

                showAlert(
                    "Email Sent",
                    "An order confirmation email has been sent.",
                    "success"
                );

                isSubmittingUpdateDetails.value = false;
            };

            onMounted(async () => {
                await getOrderDetails();
            });

            return { order, isSubmittingUpdateDetails, sendOrderEmail };
        },
    });
</script>

<style scoped>
    strong {
        font-weight: 600;
    }

    h3 {
        font-size: 1.8em;
        color: #726e6e;
    }

    .result-title {
        position: relative;
        display: flex;
        justify-content: space-between;
        background-color: var(--clr-primary);
        padding: 1rem;
        margin-inline: 2rem;
        color: white;
        border-radius: 3px;
        box-shadow: 0 10px 30px -12px rgba(0, 0, 0, 0.42), 0 4px 25px 0px rgba(0, 0, 0, 0.12), 0 8px 10px -5px rgba(0, 0, 0, 0.2);
        align-items: center;
        z-index: 1;
        margin-top: 3rem;
    }

        .result-title h1 {
            font-size: 24px;
        }

    label {
        padding-top: 1rem;
    }

    input {
        padding: 1rem;
    }

    .order-grid {
        display: grid;
        gap: 2rem;
    }

    .content {
        overflow-x: auto;
        position: relative;
        background-color: #fff;
        padding: 80px 40px 20px 40px;
        box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.14);
        border-radius: 3px;
        margin-top: -40px;
        margin-inline: 10px;
        margin-bottom: 20px;
    }

    .section {
        display: grid;
    }

    .entrySummary {
        margin-bottom: 10px;
    }

    .payment-label {
        display: block;
        font-weight: bold;
        margin-top: 1rem;
        text-transform: uppercase;
    }

    .payment-input {
        background-color: #f7f7f7;
        border: 1px solid #ddd;
        padding: 10px;
        font-size: 0.95rem;
        width: 100%;
        margin-bottom: 0.5rem;
    }

    .user {
        color: white;
        text-decoration: none;
        align-self: center;
    }

        .user svg {
            fill: white;
            width: 20px;
            height: 20px;
        }

    .btn {
        background-color: var(--clr-primary);
        padding: 1.3rem 1rem;
        color: white;
        font-size: 24px;
        border-radius: 3px;
        border: none;
        max-width: 400px;
        height: 69px;
        cursor: pointer;
        margin: 10px;
    }
</style>
